import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useBoolean } from '@uifabric/react-hooks';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IButtonStyles,
    IconButton,
    IIconStyles,
    ITooltipProps,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    TooltipHost,
} from '@fluentui/react';
import { usePromise } from '../hooks/usePromise';
import { buttonStyles } from '../util/CommonStyles';
import { ButtonState } from '../models/ButtonState';
import { CancelToken } from 'axios';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../util/localStorageUtils';

interface IIconActionButtonProps {
    onClick: (cancelToken?: CancelToken) => Promise<any>;
    initialButtonText: string;
    initialButtonIcon: string;
    showConfirmationPopup: boolean;
    modalText?: string;
    setDisabledOnSuccess?: boolean;
    setDisabledOnFailure?: boolean;
    buttonStyle?: Partial<IButtonStyles>;
    setDisabled?: boolean;
    toolTipText?: string;
    toolTipTextColor?: string;
    iconName: string;
    failedIconName?: string;
    differentiator?: string;
    disableState?: boolean;
    onHover?: string;
    iconColor?: string;
}

export const IconActionButton: React.FC<IIconActionButtonProps> = (
    props: IIconActionButtonProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
    const [buttonState, setButtonState] = useState<ButtonState>(
        ButtonState.Initialized
    );

    const disabled =
        (props.setDisabled != null
            ? props.setDisabled
            : buttonState === ButtonState.Pending ||
              (buttonState === ButtonState.Succeeded &&
                  props.setDisabledOnSuccess === true)) ||
        (buttonState === ButtonState.Failed &&
            props.setDisabledOnFailure === true);

    const modalProps = {
        isBlocking: false,
        styles: { main: { maxWidth: 450 } },
    };
    const dialogContentProps = {
        type: DialogType.largeHeader,
        title: props.initialButtonText,
        subText: props.modalText || '',
        styles: {
            subText: { whiteSpace: 'pre-line' },
        },
    };

    const getButtonIconName = (): string => {
        if (props.disableState) {
            return props.iconName;
        }
        switch (buttonState as ButtonState) {
            case ButtonState.Initialized:
                return props.iconName;
            case ButtonState.Pending:
                return 'Clock';
            case ButtonState.Succeeded:
                return 'Completed';
            case ButtonState.Failed:
                return props.failedIconName ?? 'Error';
        }
    };

    const getButtonIconTextColor = (): string => {
        if (disabled && buttonState !== ButtonState.Succeeded) {
            return theme.semanticColors.disabledText;
        }

        if (props.iconColor) {
            return props.iconColor;
        }

        if (props.disableState) {
            return theme.semanticColors.disabledText;
        }

        switch (buttonState as ButtonState) {
            case ButtonState.Initialized:
                return theme.palette.black;
            case ButtonState.Pending:
                return theme.palette.themePrimary;
            case ButtonState.Succeeded:
                return theme.semanticColors.successIcon;
            case ButtonState.Failed:
                return theme.semanticColors.errorIcon;
        }
    };

    const iconStyles: Partial<IIconStyles> = {
        root: {
            color: getButtonIconTextColor(),
            selectors: {
                ':hover': {
                    color: props.onHover, // icon turns red on hover
                    background: 'transparent',
                },
            },
        },
    };

    const tooltipProps: ITooltipProps =
        props.toolTipText != undefined
            ? {
                  onRenderContent: () =>
                      props.toolTipText != '' ? (
                          <div
                              style={{
                                  color:
                                      props.toolTipTextColor ||
                                      theme.semanticColors.errorText,
                                  whiteSpace: 'pre-line',
                              }}
                          >
                              {props.toolTipText}
                          </div>
                      ) : null,
              }
            : {
                  onRenderContent: () =>
                      error ? (
                          <div
                              style={{
                                  color: theme.semanticColors.errorText,
                              }}
                          >
                              {error.message}
                          </div>
                      ) : null,
              };

    const [, error, isLoaded, callApi] = usePromise(props.onClick, false);

    const intializeAction = () => {
        setButtonState(ButtonState.Pending);
        if (props.showConfirmationPopup) {
            toggleHideDialog();
        } else {
            activateAction();
        }
    };

    const activateAction = () => {
        toggleHideDialog();

        // Run the apiCall
        callApi();
    };

    const cancelDialog = () => {
        toggleHideDialog();
        setButtonState(ButtonState.Initialized);
    };

    useEffect(() => {
        if (isLoaded) {
            setButtonState(ButtonState.Succeeded);
            // wait for 10 seconds then change back to initialized state
            if (!props.setDisabledOnSuccess) {
                setTimeout(() => {
                    setButtonState(ButtonState.Initialized);
                }, 1000);
            }
        }
    }, [isLoaded, props.setDisabledOnSuccess]);

    useEffect(() => {
        if (error) {
            setButtonState(ButtonState.Failed);
        }
    }, [error]);

    return (
        <div className="IconActionButton-root">
            <TooltipHost tooltipProps={tooltipProps} id="errorTooltip">
                <IconButton
                    text={props.initialButtonText}
                    ariaLabel={props.toolTipText || props.initialButtonText}
                    disabled={disabled}
                    onClick={intializeAction}
                    iconProps={{
                        iconName: getButtonIconName(),
                        styles: iconStyles,
                    }}
                    onRenderIcon={
                        buttonState === ButtonState.Pending
                            ? () => (
                                  <Spinner
                                      size={SpinnerSize.small}
                                      styles={{
                                          circle: {
                                              borderTopColor:
                                                  theme.palette.themePrimary,
                                          },
                                      }}
                                  />
                              )
                            : undefined
                    }
                />
            </TooltipHost>

            {props.showConfirmationPopup && (
                <Dialog
                    hidden={hideDialog}
                    onDismiss={cancelDialog}
                    dialogContentProps={dialogContentProps}
                    modalProps={modalProps}
                >
                    <DialogFooter>
                        <PrimaryButton onClick={activateAction} text="Yes" />
                        <DefaultButton onClick={cancelDialog} text="No" />
                    </DialogFooter>
                </Dialog>
            )}
        </div>
    );
};

IconActionButton.defaultProps = {
    buttonStyle: buttonStyles,
};
